import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Plus, Building2, Loader2, KeyRound, CheckCircle2, XCircle, Power, PowerOff, FileText, Search } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import { listSucursales } from "@/services/sucursalesService";
import DashboardLayout from "@/components/DashboardLayout";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Input } from "@/components/ui/input";
import { RFCField } from "@/components/ui/forms";
import { Label } from "@/components/ui/label";
import { fmtDate } from "@/lib/datetime";
import { TIMEZONES } from "@/lib/timezones";

const empty = {
  id_empresa: null, nombre: "", rfc: "", razon_social: "", regimen_fiscal: "", plan_contratado: "",
  zona_horaria: "America/Mexico_City",
  calle_numero: "", codigo_postal: "", colonia: "", municipio: "", estado_direccion: "",
};

// Catálogo oficial del SAT — Régimen Fiscal (CFDI 4.0)
const REGIMENES_SAT = [
  { value: "601", label: "601 - General de Ley Personas Morales" },
  { value: "603", label: "603 - Personas Morales con Fines no Lucrativos" },
  { value: "605", label: "605 - Sueldos y Salarios e Ingresos Asimilados a Salarios" },
  { value: "606", label: "606 - Arrendamiento" },
  { value: "607", label: "607 - Régimen de Enajenación o Adquisición de Bienes" },
  { value: "608", label: "608 - Demás ingresos" },
  { value: "610", label: "610 - Residentes en el Extranjero sin Establecimiento Permanente en México" },
  { value: "611", label: "611 - Ingresos por Dividendos (socios y accionistas)" },
  { value: "612", label: "612 - Personas Físicas con Actividades Empresariales y Profesionales" },
  { value: "614", label: "614 - Ingresos por intereses" },
  { value: "615", label: "615 - Régimen de los ingresos por obtención de premios" },
  { value: "616", label: "616 - Sin obligaciones fiscales" },
  { value: "620", label: "620 - Sociedades Cooperativas de Producción que optan por diferir sus ingresos" },
  { value: "621", label: "621 - Incorporación Fiscal" },
  { value: "622", label: "622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras" },
  { value: "623", label: "623 - Opcional para Grupos de Sociedades" },
  { value: "624", label: "624 - Coordinados" },
  { value: "625", label: "625 - Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas" },
  { value: "626", label: "626 - Régimen Simplificado de Confianza (RESICO)" },
];

const ExpRow = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 px-4 py-3">
    <span className="text-[11px] uppercase tracking-wider font-bold text-[#52525B] shrink-0">{label}</span>
    <span className="text-sm text-[#0A0A0A] text-right">{value}</span>
  </div>
);

const IntegRow = ({ label, on }) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3">
    <span className="text-sm font-semibold text-[#0A0A0A]">{label}</span>
    {on ? (
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#047857]"><CheckCircle2 className="h-4 w-4" /> Conectado</span>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#a1a1aa]"><XCircle className="h-4 w-4" /> No configurado</span>
    )}
  </div>
);

export default function Empresas() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [exp, setExp] = useState(null);
  const [expTab, setExpTab] = useState("resumen");
  const [coloniaOpts, setColoniaOpts] = useState([]);
  const [cpLoading, setCpLoading] = useState(false);
  const [expSucursales, setExpSucursales] = useState(null);
  const [confirmEmp, setConfirmEmp] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/empresas");
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const buscarCP = async (cp) => {
    if (!/^\d{5}$/.test(cp)) { setColoniaOpts([]); return; }
    setCpLoading(true);
    try {
      const res = await fetch(`https://api.zippopotam.us/mx/${cp}`);
      if (!res.ok) throw new Error("not found");
      const d = await res.json();
      const places = d.places || [];
      const estado = places[0]?.state || "";
      const colonias = [...new Set(places.map((p) => p["place name"]).filter(Boolean))];
      const municipio = places[0]?.["state abbreviation"] ? (places[0]?.["place name"] || "") : (places[0]?.["place name"] || "");
      setColoniaOpts(colonias);
      setForm((f) => ({ ...f, estado_direccion: estado, municipio, colonia: colonias[0] || "" }));
      toast.success("CP localizado: se autocompletó Estado, Municipio y Colonias");
    } catch {
      setColoniaOpts([]);
      toast.error("CP no encontrado. Captura Estado/Municipio/Colonia manualmente.");
    } finally { setCpLoading(false); }
  };

  const abrirNueva = () => { setForm(empty); setColoniaOpts([]); setOpen(true); };

  const abrirEditar = (e) => {
    // Sprint 6: la dirección ya se persiste por campos individuales (CFDI 4.0), sin parseo
    setForm({
      id_empresa: e.id_empresa, nombre: e.nombre || "", rfc: e.rfc || "",
      razon_social: e.razon_social || "", regimen_fiscal: e.regimen_fiscal || "",
      plan_contratado: e.plan_contratado || "", codigo_postal: e.codigo_postal || "",
      calle_numero: e.calle_numero || "", colonia: e.colonia || "", municipio: e.municipio || "", estado_direccion: e.estado_direccion || "",
      zona_horaria: e.zona_horaria || "America/Mexico_City",
    });
    setColoniaOpts([]);
    setExpOpen(false);
    setOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    // Sprint 6: se envían los componentes de dirección por separado (CFDI 4.0)
    const payload = {
      nombre: form.nombre.trim(),
      rfc: form.rfc || null,
      razon_social: form.razon_social || null,
      regimen_fiscal: form.regimen_fiscal || null,
      plan_contratado: form.plan_contratado || null,
      codigo_postal: form.codigo_postal || null,
      calle_numero: form.calle_numero || null,
      colonia: form.colonia || null,
      municipio: form.municipio || null,
      estado_direccion: form.estado_direccion || null,
      zona_horaria: form.zona_horaria || "America/Mexico_City",
    };
    try {
      if (form.id_empresa) {
        await api.put(`/empresas/${form.id_empresa}`, payload);
        toast.success("Empresa actualizada");
      } else {
        await api.post("/empresas", payload);
        toast.success("Empresa creada");
      }
      setOpen(false); setForm(empty); setColoniaOpts([]); load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const toggleEstatus = async (e) => {
    try {
      if (e.estatus === "suspendido") {
        await api.post(`/empresas/${e.id_empresa}/reactivar`);
        toast.success("Empresa reactivada");
      } else {
        await api.delete(`/empresas/${e.id_empresa}`);
        toast.success("Empresa suspendida");
      }
      const fresh = await api.get("/empresas");
      setItems(fresh.data);
      if (exp && exp.id_empresa === e.id_empresa) {
        setExp(fresh.data.find((x) => x.id_empresa === e.id_empresa) || null);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const abrirExpediente = async (e, tab = "resumen") => {
    setExp(e); setExpTab(tab); setExpOpen(true); setExpSucursales(null);
    try {
      const { data } = await listSucursales({ params: { empresa_id: e.id_empresa } });
      setExpSucursales(data);
    } catch { setExpSucursales([]); }
  };

  const fmtFecha = (v) => fmtDate(v, { year: "numeric", month: "long", day: "numeric" });

  const q = query.trim().toLowerCase();
  const filtered = items.filter((e) => {
    const matchQ = !q || (e.nombre || "").toLowerCase().includes(q) || (e.rfc || "").toLowerCase().includes(q);
    const est = e.estatus || "activo";
    const matchEst = statusFilter === "todos" || est === statusFilter;
    return matchQ && matchEst;
  });

  // Conteos por estatus (respetan la búsqueda actual para un desglose coherente)
  const byQuery = items.filter((e) => !q || (e.nombre || "").toLowerCase().includes(q) || (e.rfc || "").toLowerCase().includes(q));
  const statusCounts = {
    todos: byQuery.length,
    activo: byQuery.filter((e) => (e.estatus || "activo") === "activo").length,
    suspendido: byQuery.filter((e) => (e.estatus || "activo") === "suspendido").length,
  };

  const SecretBadge = ({ on, label }) => (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold">
      {on ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10B981]" /> : <XCircle className="h-3.5 w-3.5 text-gray-300" />}
      {label}
    </span>
  );

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2">Catálogo de inquilinos</p>
            <h1 className="font-display text-4xl font-black tracking-tight">Empresas</h1>
          </div>
          <button onClick={abrirNueva} data-testid="create-empresa-btn"
            className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center gap-2">
            <Plus className="h-4 w-4" /> Nueva empresa
          </button>
        </div>

        <div className="mb-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative w-full max-w-md">
            <Search className="h-4 w-4 text-[#52525B] absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              value={query}
              onChange={(ev) => setQuery(ev.target.value)}
              data-testid="empresas-search-input"
              placeholder="Buscar por nombre o RFC…"
              className="h-11 pl-9 rounded-none border-[#0A0A0A]"
            />
            {query && (
              <button onClick={() => setQuery("")} data-testid="empresas-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-[#0A0A0A]">
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex border border-[#0A0A0A]" data-testid="empresas-status-filter">
            {[
              { v: "todos", label: "Todas" },
              { v: "activo", label: "Activas" },
              { v: "suspendido", label: "Suspendidas" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setStatusFilter(opt.v)}
                data-testid={`status-filter-${opt.v}`}
                className={`h-11 px-4 text-xs font-bold uppercase tracking-wider transition-colors duration-200 border-r border-[#0A0A0A] last:border-r-0 flex items-center gap-1.5 ${statusFilter === opt.v ? "bg-[#0A0A0A] text-white" : "bg-white text-[#52525B] hover:bg-[#f4f4f5]"}`}
              >
                {opt.label}
                <span data-testid={`status-count-${opt.v}`} className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 text-[10px] font-mono font-bold border ${statusFilter === opt.v ? "border-white/40 text-white" : "border-[#e4e4e7] text-[#0A0A0A]"}`}>
                  {statusCounts[opt.v]}
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#e4e4e7]">
          {loading ? <div className="p-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
          : items.length === 0 ? <div className="p-16 text-center text-[#52525B]"><Building2 className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin empresas registradas.</p></div>
          : filtered.length === 0 ? <div className="p-16 text-center text-[#52525B]" data-testid="empresas-no-results"><Search className="h-8 w-8 mx-auto mb-3 opacity-40" /><p className="text-sm">Sin resultados{query ? <> para "<span className="font-semibold text-[#0A0A0A]">{query}</span>"</> : null} con el filtro seleccionado.</p></div>
          : (
            <table className="w-full text-sm" data-testid="empresas-table">
              <thead><tr className="border-b border-[#e4e4e7] text-left">
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Empresa</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">RFC</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Estatus</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Sucursales</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase">Credenciales</th>
                <th className="font-display font-bold px-5 py-4 text-xs tracking-wider uppercase text-right">Acciones</th>
              </tr></thead>
              <tbody>
                {filtered.map((e) => (
                  <tr key={e.id_empresa} className="border-b border-[#f0f0f1] hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-5 py-4">
                      <button onClick={() => abrirExpediente(e)} data-testid={`empresa-open-${e.id_empresa}`}
                        className="font-semibold text-[#0A0A0A] hover:text-[#2563EB] underline decoration-transparent hover:decoration-[#2563EB] underline-offset-4 transition-colors duration-200 inline-flex items-center gap-2">
                        <FileText className="h-3.5 w-3.5 opacity-60" /> {e.nombre}
                      </button>
                    </td>
                    <td className="px-5 py-4 text-[#52525B]">{e.rfc || "—"}</td>
                    <td className="px-5 py-4">
                      <span data-testid={`empresa-estatus-${e.id_empresa}`} className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-2 py-1 border ${e.estatus === "suspendido" ? "text-[#B45309] border-[#FED7AA] bg-[#FFFBEB]" : "text-[#047857] border-[#A7F3D0] bg-[#ECFDF5]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${e.estatus === "suspendido" ? "bg-[#F59E0B]" : "bg-[#10B981]"}`} />
                        {e.estatus || "activo"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <button onClick={() => abrirExpediente(e, "sucursales")} data-testid={`empresa-sucursales-${e.id_empresa}`}
                        className="inline-flex items-center gap-1.5 text-xs font-bold font-mono px-2.5 py-1 border border-[#e4e4e7] bg-[#FAFAFA] text-[#0A0A0A] hover:border-[#FFCC00] hover:bg-[#FFFBEB] transition-colors duration-200 cursor-pointer">
                        <Building2 className="h-3.5 w-3.5 text-[#52525B]" /> {e.sucursales_count ?? 0}
                      </button>
                    </td>
                    <td className="px-5 py-4"><div className="flex flex-col gap-1">
                      <SecretBadge on={e.tiene_mp_token || e.mp_conectado} label="Mercado Pago" />
                      <SecretBadge on={e.tiene_taecel_key} label="TAECEL" />
                      <SecretBadge on={e.tiene_prontipagos_key} label="Prontipagos" />
                    </div></td>
                    <td className="px-5 py-4 text-right">
                      <button onClick={() => setConfirmEmp(e)} data-testid={`toggle-empresa-${e.id_empresa}`}
                        className={`h-8 px-3 inline-flex items-center gap-1.5 border text-[11px] font-bold uppercase tracking-wider transition-colors duration-200 ${e.estatus === "suspendido" ? "border-[#e4e4e7] text-[#047857] hover:border-[#10B981]" : "border-[#e4e4e7] text-[#B45309] hover:border-[#F59E0B]"}`}>
                        {e.estatus === "suspendido" ? <><Power className="h-3.5 w-3.5" /> Reactivar</> : <><PowerOff className="h-3.5 w-3.5" /> Suspender</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </motion.div>

      {/* Expediente de Empresa */}
      <Sheet open={expOpen} onOpenChange={setExpOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="expediente-sheet">
          {exp && (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
                  <Building2 className="h-6 w-6 text-[#FFCC00]" /> {exp.nombre}
                </SheetTitle>
                <SheetDescription>Código operativo: <span className="font-mono">{exp.codigo_operativo}</span></SheetDescription>
              </SheetHeader>

              <Tabs value={expTab} onValueChange={setExpTab} className="mt-6">
                <TabsList className="grid grid-cols-4 w-full rounded-none bg-[#f4f4f5]">
                  <TabsTrigger value="resumen" data-testid="tab-resumen" className="rounded-none text-xs font-bold uppercase tracking-wide">Resumen</TabsTrigger>
                  <TabsTrigger value="fiscales" data-testid="tab-fiscales" className="rounded-none text-xs font-bold uppercase tracking-wide">Fiscales</TabsTrigger>
                  <TabsTrigger value="integraciones" data-testid="tab-integraciones" className="rounded-none text-xs font-bold uppercase tracking-wide">Integraciones</TabsTrigger>
                  <TabsTrigger value="sucursales" data-testid="tab-sucursales" className="rounded-none text-xs font-bold uppercase tracking-wide">Sucursales</TabsTrigger>
                </TabsList>

                <TabsContent value="resumen" className="mt-5" data-testid="content-resumen">
                  <div className="divide-y divide-[#f0f0f1] border border-[#e4e4e7]">
                    <ExpRow label="Nombre comercial" value={exp.nombre} />
                    <ExpRow label="RFC" value={exp.rfc || "—"} />
                    <ExpRow label="Plan contratado" value={exp.plan_contratado || "Sin plan"} />
                    <ExpRow label="Estatus" value={
                      <span className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold ${exp.estatus === "suspendido" ? "text-[#B45309]" : "text-[#047857]"}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${exp.estatus === "suspendido" ? "bg-[#F59E0B]" : "bg-[#10B981]"}`} /> {exp.estatus || "activo"}
                      </span>
                    } />
                    <ExpRow label="Fecha de alta" value={fmtFecha(exp.creado_en)} />
                  </div>
                </TabsContent>

                <TabsContent value="fiscales" className="mt-5" data-testid="content-fiscales">
                  <div className="divide-y divide-[#f0f0f1] border border-[#e4e4e7]">
                    <ExpRow label="Razón social" value={exp.razon_social || "—"} />
                    <ExpRow label="Régimen fiscal" value={exp.regimen_fiscal || "—"} />
                    <ExpRow label="Calle y número" value={exp.calle_numero || "—"} />
                    <ExpRow label="Colonia" value={exp.colonia || "—"} />
                    <ExpRow label="Municipio / Alcaldía" value={exp.municipio || "—"} />
                    <ExpRow label="Estado" value={exp.estado_direccion || "—"} />
                    <ExpRow label="Código postal" value={exp.codigo_postal || "—"} />
                  </div>
                </TabsContent>

                <TabsContent value="integraciones" className="mt-5" data-testid="content-integraciones">
                  <div className="divide-y divide-[#f0f0f1] border border-[#e4e4e7]">
                    <IntegRow label="Mercado Pago" on={exp.tiene_mp_token || exp.mp_conectado} />
                    <IntegRow label="TAECEL" on={exp.tiene_taecel_key} />
                    <IntegRow label="ProntiPagos" on={exp.tiene_prontipagos_key} />
                  </div>
                  <p className="mt-3 text-[11px] text-[#52525B] flex items-center gap-1.5">
                    <KeyRound className="h-3.5 w-3.5" /> Por seguridad, las llaves y tokens nunca se muestran en texto plano.
                  </p>
                </TabsContent>

                <TabsContent value="sucursales" className="mt-5" data-testid="content-sucursales">
                  {expSucursales === null ? (
                    <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#FFCC00]" /></div>
                  ) : expSucursales.length === 0 ? (
                    <div className="border border-dashed border-[#d4d4d8] p-10 text-center" data-testid="sucursales-empty">
                      <Building2 className="h-8 w-8 mx-auto mb-3 opacity-30" />
                      <p className="text-sm font-bold text-[#52525B]">Sin sucursales registradas</p>
                      <p className="text-xs text-[#a1a1aa] mt-1">El TenantAdmin de esta empresa aún no ha creado sucursales.</p>
                    </div>
                  ) : (
                    <div className="border border-[#e4e4e7] divide-y divide-[#f0f0f1]" data-testid="sucursales-list">
                      {expSucursales.map((s) => {
                        const mods = [];
                        if (s.modulo_estacionamiento) mods.push("Estacionamiento");
                        if (s.modulo_valet) mods.push("Valet");
                        if (s.cobro_tarjeta_mp) mods.push("Tarjeta MP");
                        if (s.cobro_recargas_taecel) mods.push("Recargas");
                        if (s.cobro_servicios_prontipagos) mods.push("Servicios");
                        return (
                          <div key={s.id_sucursal} className="px-4 py-3 flex items-start justify-between gap-4" data-testid={`sucursal-row-${s.id_sucursal}`}>
                            <div className="min-w-0">
                              <p className="font-semibold text-[#0A0A0A] truncate">{s.nombre}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {mods.length ? mods.map((m) => (
                                  <span key={m} className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border border-[#e4e4e7] text-[#52525B]">{m}</span>
                                )) : <span className="text-[11px] text-[#a1a1aa]">Sin módulos activos</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-[10px] uppercase tracking-wider font-bold text-[#a1a1aa]">Folios</p>
                              <p className="font-display font-black text-[#0A0A0A]" data-testid={`sucursal-folios-${s.id_sucursal}`}>{s.saldo_folios ?? 0}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-3 text-[11px] text-[#52525B]">Vista de sólo lectura. La gestión de sucursales corresponde al TenantAdmin.</p>
                </TabsContent>
              </Tabs>

              <button onClick={() => setConfirmEmp(exp)} data-testid="expediente-toggle-btn"
                className={`btn-hard w-full mt-6 h-11 border font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 ${exp.estatus === "suspendido" ? "bg-[#10B981] text-white border-[#10B981]" : "bg-white text-[#B45309] border-[#F59E0B]"}`}>
                {exp.estatus === "suspendido" ? <><Power className="h-4 w-4" /> Reactivar empresa</> : <><PowerOff className="h-4 w-4" /> Suspender empresa</>}
              </button>
              <button onClick={() => abrirEditar(exp)} data-testid="expediente-editar-btn"
                className="btn-hard w-full mt-3 h-11 bg-[#0A0A0A] text-white border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2">
                <FileText className="h-4 w-4" /> Editar expediente
              </button>
            </>
          )}
        </SheetContent>
      </Sheet>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="empresa-form-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl font-black tracking-tight">{form.id_empresa ? "Editar empresa" : "Nueva empresa"}</SheetTitle>
            <SheetDescription>Datos del expediente del inquilino. Las credenciales financieras NO se gestionan aquí.</SheetDescription>
          </SheetHeader>
          <div className="space-y-5 mt-6">
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Nombre comercial</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="empresa-nombre" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">RFC</Label>
                <RFCField value={form.rfc} onChange={(v) => setForm({ ...form, rfc: v })} data-testid="empresa-rfc" className="h-11 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Plan contratado</Label>
                <Input value={form.plan_contratado} onChange={(e) => setForm({ ...form, plan_contratado: e.target.value })} data-testid="empresa-plan" className="h-11 rounded-none border-[#0A0A0A]" placeholder="Ej. Básico, Pro" /></div>
            </div>
            <div className="space-y-2" data-testid="empresa-zona-horaria-block"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Zona horaria (por defecto del Tenant)</Label>
              <Select value={form.zona_horaria} onValueChange={(v) => setForm({ ...form, zona_horaria: v })}>
                <SelectTrigger data-testid="empresa-zona-horaria" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value} data-testid={`empresa-tz-${tz.value}`}>{tz.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#A1A1AA]">Se usa cuando una sucursal no define su propia zona. Cada sucursal puede sobreescribirla.</p></div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Razón social</Label>
              <Input value={form.razon_social} onChange={(e) => setForm({ ...form, razon_social: e.target.value })} data-testid="empresa-razon-social" className="h-11 rounded-none border-[#0A0A0A]" /></div>
            <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Régimen fiscal</Label>
              <Select value={form.regimen_fiscal} onValueChange={(v) => setForm({ ...form, regimen_fiscal: v })}>
                <SelectTrigger data-testid="empresa-regimen" className="h-11 rounded-none border-[#0A0A0A]"><SelectValue placeholder="Selecciona el régimen del SAT" /></SelectTrigger>
                <SelectContent>
                  {REGIMENES_SAT.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`regimen-opt-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select></div>

            <div className="border border-[#e4e4e7] p-4 space-y-4">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Dirección</p>
              <div className="space-y-2"><Label className="text-[11px] uppercase font-bold text-[#52525B]">Calle y número</Label>
                <Input value={form.calle_numero} onChange={(e) => setForm({ ...form, calle_numero: e.target.value })} data-testid="empresa-calle" className="h-10 rounded-none border-[#0A0A0A]" /></div>
              <div className="space-y-2"><Label className="text-[11px] uppercase font-bold text-[#52525B] flex items-center gap-2">Código postal {cpLoading && <Loader2 className="h-3 w-3 animate-spin" />}</Label>
                <Input value={form.codigo_postal} maxLength={5}
                  onChange={(e) => { const cp = e.target.value.replace(/\D/g, "").slice(0, 5); setForm({ ...form, codigo_postal: cp }); if (cp.length === 5) buscarCP(cp); }}
                  data-testid="empresa-cp" className="h-10 rounded-none border-[#0A0A0A]" placeholder="5 dígitos — autocompleta ubicación" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label className="text-[11px] uppercase font-bold text-[#52525B]">Estado</Label>
                  <Input value={form.estado_direccion} onChange={(e) => setForm({ ...form, estado_direccion: e.target.value })} data-testid="empresa-estado" className="h-10 rounded-none border-[#0A0A0A]" /></div>
                <div className="space-y-2"><Label className="text-[11px] uppercase font-bold text-[#52525B]">Municipio</Label>
                  <Input value={form.municipio} onChange={(e) => setForm({ ...form, municipio: e.target.value })} data-testid="empresa-municipio" className="h-10 rounded-none border-[#0A0A0A]" /></div>
              </div>
              <div className="space-y-2"><Label className="text-[11px] uppercase font-bold text-[#52525B]">Colonia</Label>
                {coloniaOpts.length > 0 ? (
                  <Select value={form.colonia} onValueChange={(v) => setForm({ ...form, colonia: v })}>
                    <SelectTrigger data-testid="empresa-colonia-select" className="h-10 rounded-none border-[#0A0A0A]"><SelectValue placeholder="Selecciona colonia" /></SelectTrigger>
                    <SelectContent>{coloniaOpts.map((c) => <SelectItem key={c} value={c} data-testid={`colonia-opt-${c}`}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input value={form.colonia} onChange={(e) => setForm({ ...form, colonia: e.target.value })} data-testid="empresa-colonia-input" className="h-10 rounded-none border-[#0A0A0A]" placeholder="Teclea el CP para ver opciones" />
                )}
              </div>
            </div>

            <button onClick={save} disabled={saving} data-testid="empresa-save-btn"
              className="btn-hard w-full h-12 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (form.id_empresa ? "Guardar cambios" : "Crear empresa")}
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmEmp !== null}
        onOpenChange={(o) => { if (!o) setConfirmEmp(null); }}
        title={confirmEmp?.estatus === "suspendido" ? "Reactivar empresa" : "Suspender empresa"}
        description={confirmEmp?.estatus === "suspendido"
          ? `¿Deseas reactivar el acceso a "${confirmEmp?.nombre}"?`
          : `¿Estás seguro de que deseas suspender el acceso a la empresa "${confirmEmp?.nombre}"? Sus usuarios no podrán operar.`}
        confirmLabel={confirmEmp?.estatus === "suspendido" ? "Reactivar" : "Suspender"}
        danger={confirmEmp?.estatus !== "suspendido"}
        onConfirm={() => { const e = confirmEmp; setConfirmEmp(null); if (e) toggleEstatus(e); }}
      />
    </DashboardLayout>
  );
}
